"use client";

import Image from "next/image";
import Link from "next/link";
import SocialLinks from "./social-links";
import { MapPin, Phone, Mail, Gift } from "lucide-react";
import { useEffect, useState } from "react";

const footerWidget = [
  {
    id: 1,
    title: "Quick Links",
    footer__links: [
      { list: "Privacy Policy", path: "/privacy-policy" },
      { list: "Terms & Condition", path: "/terms-and-conditions" },
      { list: "Returns & Refunds", path: "/returns-and-refunds" },
    ],
  },
  {
    id: 2,
    title: "Customer Services",
    footer__links: [
      { list: "Help & Contact Us", path: "/contact" },
      { list: "Gift Store", path: "/shop" },
      { list: "FAQs", path: "/faq" },
    ],
  },
];

export default function Footer() {
  const [sparkles, setSparkles] = useState<any[]>([]);

  // Floating sparkles generation
  useEffect(() => {
    const count = 40;
    const emojis = ["🎁", "✨", "🎀", "💝", "⭐"];
    const sparkleArray = Array.from({ length: count }).map(() => ({
      left: Math.random() * 100,
      size: 12 + Math.random() * 8,
      duration: 6 + Math.random() * 8,
      delay: Math.random() * 4,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
      opacity: 0.15 + Math.random() * 0.25,
    }));

    setSparkles(sparkleArray);
  }, []);

  const sparkleKeyframes = `
    @keyframes sparkleFooter {
      0% { transform: translateY(-10vh) rotate(0deg); opacity: 0; }
      10% { opacity: var(--opacity); }
      90% { opacity: var(--opacity); }
      100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
    }
  `;

  return (
    <>
      <style>{sparkleKeyframes}</style>

      <footer
        className="gift-footer"
        style={{
          background: "linear-gradient(135deg, #FFF9F5 0%, #FFFBF8 50%, #FFF5EE 100%)",
          color: "#2C2C2C",
          paddingTop: "100px",
          marginTop: "80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative background circles */}
        <div
          style={{
            position: "absolute",
            top: "-100px",
            right: "-150px",
            width: "400px",
            height: "400px",
            background: "radial-gradient(circle, rgba(255,182,193,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-80px",
            left: "-120px",
            width: "350px",
            height: "350px",
            background: "radial-gradient(circle, rgba(255,215,180,0.12) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* Floating sparkles */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          {sparkles.map((s, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                top: "-10vh",
                left: `${s.left}%`,
                fontSize: `${s.size}px`,
                opacity: s.opacity,
                animation: `sparkleFooter ${s.duration}s linear infinite`,
                animationDelay: `${s.delay}s`,
                // @ts-ignore
                "--opacity": s.opacity,
              }}
            >
              {s.emoji}
            </div>
          ))}
        </div>

        {/* Main Footer Container */}
        <div
          className="footer-container"
          style={{
            maxWidth: "1300px",
            margin: "0 auto",
            padding: "0 24px",
            position: "relative",
            zIndex: 5,
          }}
        >
          <div
            className="footer-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "40px",
              marginBottom: "80px",
            }}
          >
            {/* Brand Section */}
            <div
              className="footer-card"
              style={{
                background: "#FFFFFF",
                borderRadius: "24px",
                padding: "32px",
                border: "2px solid #FFF0E6",
                boxShadow: `
                  0 2px 4px rgba(255, 255, 255, 0.08),
                  0 4px 8px rgba(255,140,83,0.1),
                  0 8px 16px rgba(255,140,83,0.12),
                  0 16px 32px rgba(63, 63, 63, 0.08)
                `,
                transition: "0.3s",
              }}
            >
              <div style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "12px",
                    background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
                    display: "grid",
                    placeItems: "center",
                    boxShadow: "0 4px 16px rgba(255,107,107,0.3)",
                  }}
                >
                  <Gift style={{ width: "28px", height: "28px", color: "#fff" }} />
                </div>
                <h2
                  className="footer-brand"
                  style={{
                    color: "#2C2C2C",
                    fontSize: "32px",
                    fontWeight: 900,
                    letterSpacing: "0.5px",
                    margin: 0,
                  }}
                >
                  Gift<span style={{ 
                    background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}>Shop</span>
                </h2>
              </div>

              <p
                style={{
                  fontSize: "15px",
                  color: "#666",
                  lineHeight: 1.7,
                  marginBottom: "22px",
                  fontWeight: 500,
                }}
              >
                Making every moment special with thoughtfully curated gifts. Celebrate life's precious moments with us.
              </p>

              <SocialLinks />
            </div>

            {/* Contact Info */}
            <div
              className="footer-card"
              style={{
                background: "#FFFFFF",
                borderRadius: "24px",
                padding: "32px",
                border: "2px solid #FFF0E6",
                boxShadow: `
                  0 2px 4px rgba(255, 255, 255, 0.08),
                  0 4px 8px rgba(255,140,83,0.1),
                  0 8px 16px rgba(255,140,83,0.12),
                  0 16px 32px rgba(63, 63, 63, 0.08)
                `,
                transition: "0.3s",
              }}
            >
              <h4
                style={{
                  color: "#2C2C2C",
                  fontSize: "20px",
                  fontWeight: 800,
                  marginBottom: "20px",
                }}
              >
                Contact Us
              </h4>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <MapPin style={{ color: "#FF6B6B", minWidth: "20px", marginTop: "2px" }} size={20} />
                  <p style={{ margin: 0, color: "#666", fontSize: "14px", lineHeight: 1.6 }}>
                    Dhaka Outlet, Bailey Road, AQP Shopping Mall
                  </p>
                </div>

                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <Phone style={{ color: "#FF6B6B" }} size={20} />
                  <a 
                    href="tel:+8801622534977" 
                    style={{ 
                      color: "#FF6B6B", 
                      fontWeight: 700,
                      textDecoration: "none",
                      fontSize: "15px",
                    }}
                  >
                    +880 1622-534977
                  </a>
                </div>

                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <Mail style={{ color: "#FF6B6B" }} size={20} />
                  <a
                    href="mailto:support@giftshop.com"
                    style={{ 
                      color: "#FF6B6B", 
                      fontWeight: 700,
                      textDecoration: "none",
                      fontSize: "15px",
                    }}
                  >
                    support@giftshop.com
                  </a>
                </div>
              </div>
            </div>

            {/* Footer Widgets */}
            {footerWidget.map((item) => (
              <div
                key={item.id}
                className="footer-card"
                style={{
                  background: "#FFFFFF",
                  borderRadius: "24px",
                  padding: "32px",
                  border: "2px solid #FFF0E6",
                  boxShadow: `
                    0 2px 4px rgba(255, 255, 255, 0.08),
                    0 4px 8px rgba(255,140,83,0.1),
                    0 8px 16px rgba(255,140,83,0.12),
                    0 16px 32px rgba(63, 63, 63, 0.08)
                  `,
                }}
              >
                <h4 
                  style={{ 
                    fontSize: "20px", 
                    fontWeight: 800, 
                    marginBottom: "20px",
                    color: "#2C2C2C"
                  }}
                > 
                  {item.title}
                </h4>

                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {item.footer__links.map((link, i) => (
                    <li key={i} style={{ marginBottom: "12px" }}>
                      <Link
                        href={link.path}
                        style={{
                          color: "#666",
                          textDecoration: "none",
                          fontSize: "15px",
                          fontWeight: 500,
                          transition: "0.3s",
                          display: "inline-block",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#FF6B6B";
                          e.currentTarget.style.marginLeft = "6px";
                          e.currentTarget.style.fontWeight = "700";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#666";
                          e.currentTarget.style.marginLeft = "0px";
                          e.currentTarget.style.fontWeight = "500";
                        }}
                      >
                        → {link.list}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Footer Bottom */}
          <div
            className="footer-bottom"
            style={{
              borderTop: "2px solid #FFE9DB",
              padding: "28px 0",
              textAlign: "center",
              color: "#666",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            <p style={{ margin: 0 }}>
              🎁 © 2025 GiftShop — Making Moments Memorable · All Rights Reserved
            </p>
          </div>
        </div>

        {/* Mobile Responsive Styles */}
        <style jsx>{`
          @media (max-width: 768px) {
            .gift-footer {
              padding-top: 60px !important;
              margin-top: 60px !important;
            }

            .footer-container {
              padding: 0 16px !important;
            }

            .footer-grid {
              grid-template-columns: 1fr !important;
              gap: 24px !important;
              margin-bottom: 50px !important;
            }

            .footer-card {
              padding: 24px !important;
              border-radius: 16px !important;
            }

            .footer-brand {
              font-size: 28px !important;
            }

            .footer-card h4 {
              font-size: 18px !important;
              margin-bottom: 16px !important;
            }

            .footer-card p {
              font-size: 14px !important;
            }

            .footer-card a {
              font-size: 14px !important;
            }

            .footer-bottom {
              padding: 20px 0 !important;
              font-size: 13px !important;
            }
          }

          @media (max-width: 480px) {
            .gift-footer {
              padding-top: 50px !important;
            }

            .footer-card {
              padding: 20px !important;
            }

            .footer-brand {
              font-size: 24px !important;
            }

            .footer-card h4 {
              font-size: 17px !important;
            }

            .footer-bottom {
              font-size: 12px !important;
            }
          }
        `}</style>
      </footer>
    </>
  );
}